/**
 * Routes — Suturia.
 * Pattern B: Layout renders <AnimatedOutlet/> and the routes nest under it.
 * Authenticated shell forces login + first-run onboarding (terms → profile → tour).
 */
import { Suspense } from 'react';
import { Outlet, Routes, Route } from 'react-router';
import { wrapReactRouterRouting } from '@sentry/react';
import { MotionConfig } from 'framer-motion';
import { SuturiaProvider } from '@/lib/store';
import { MotionProvider } from '@/components/MotionProvider';
import Layout from '@/components/Layout';
import { AppToaster } from '@/components/AppToaster';
import { PwaUpdateBanner } from '@/components/PwaUpdateBanner';
import { RequireAuth } from '@/components/RequireAuth';
import { RequireOnboarding } from '@/components/RequireOnboarding';
import { RouteFallback } from '@/components/RouteFallback';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { lazyRetry } from '@/lib/lazy-retry';

const SentryRoutes = wrapReactRouterRouting(Routes);

const Dashboard = lazyRetry(() => import('@/pages/Dashboard'));
const Library = lazyRetry(() => import('@/pages/Library'));
const Account = lazyRetry(() => import('@/pages/Account'));
const NewSurgery = lazyRetry(() => import('@/pages/NewSurgery'));
const SurgeryDetail = lazyRetry(() => import('@/pages/SurgeryDetail'));
const AnnualReport = lazyRetry(() => import('@/pages/AnnualReport'));
const Profile = lazyRetry(() => import('@/pages/Profile'));
const Settings = lazyRetry(() => import('@/pages/Settings'));
const Legal = lazyRetry(() => import('@/pages/Legal'));
const Guide = lazyRetry(() => import('@/pages/Guide'));
const Features = lazyRetry(() => import('@/pages/Features'));
const Landing = lazyRetry(() => import('@/pages/Landing'));
const Procedures = lazyRetry(() => import('@/pages/Procedures'));
const Community = lazyRetry(() => import('@/pages/Community'));
const CommunityPost = lazyRetry(() => import('@/pages/CommunityPost'));
const CommunityPostForm = lazyRetry(() => import('@/pages/CommunityPostForm'));
const Research = lazyRetry(() => import('@/pages/Research'));
const ClinicalDatasets = lazyRetry(() => import('@/pages/ClinicalDatasets'));
const OnboardingTerms = lazyRetry(() => import('@/pages/onboarding/OnboardingTerms'));
const OnboardingProfile = lazyRetry(() => import('@/pages/onboarding/OnboardingProfile'));
/** Auth pages are rare for signed-in sessions — keep them out of the main graph. */
const Login = lazyRetry(() => import('@/pages/Login'));
const ForgotPassword = lazyRetry(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazyRetry(() => import('@/pages/ResetPassword'));
const NotFound = lazyRetry(() => import('@/pages/NotFound'));

function AuthenticatedShell() {
  return (
    // Signed out at `/`: the public landing page (the indexed entry point)
    // instead of a redirect to /login. Any other gated path still redirects.
    <RequireAuth signedOutFallback={<Page><Landing /></Page>}>
      <RequireOnboarding>
        <Outlet />
      </RequireOnboarding>
    </RequireAuth>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  return <RouteErrorBoundary>{children}</RouteErrorBoundary>;
}

export default function App() {
  return (
    <SuturiaProvider>
      <MotionProvider>
      <MotionConfig reducedMotion="user">
        <Suspense fallback={<RouteFallback />}>
          <SentryRoutes>
            <Route element={<AuthenticatedShell />}>
              <Route path="onboarding/terms" element={<Page><OnboardingTerms /></Page>} />
              <Route path="onboarding/profile" element={<Page><OnboardingProfile /></Page>} />
              <Route element={<Layout />}>
                <Route index element={<Page><Dashboard /></Page>} />
                <Route path="library" element={<Page><Library /></Page>} />
                <Route path="procedures" element={<Page><Procedures /></Page>} />
                <Route path="community" element={<Page><Community /></Page>} />
                <Route path="community/new" element={<Page><CommunityPostForm /></Page>} />
                <Route path="community/:id/edit" element={<Page><CommunityPostForm /></Page>} />
                <Route path="community/:id" element={<Page><CommunityPost /></Page>} />
                <Route path="research" element={<Page><Research /></Page>} />
                <Route path="research/datasets" element={<Page><ClinicalDatasets /></Page>} />
                <Route path="research/datasets/:workspaceId/tables/:tableId" element={<Page><ClinicalDatasets /></Page>} />
                <Route path="research/datasets/:workspaceId" element={<Page><ClinicalDatasets /></Page>} />
                <Route path="research/:id" element={<Page><Research /></Page>} />
                <Route path="account" element={<Page><Account /></Page>} />
                <Route path="profile" element={<Page><Profile /></Page>} />
                <Route path="settings" element={<Page><Settings /></Page>} />
                <Route path="new" element={<Page><NewSurgery /></Page>} />
                <Route path="edit/:id" element={<Page><NewSurgery /></Page>} />
                <Route path="surgery/:id" element={<Page><SurgeryDetail /></Page>} />
                <Route path="report" element={<Page><AnnualReport /></Page>} />
                <Route path="*" element={<Page><Dashboard /></Page>} />
              </Route>
            </Route>
            <Route path="/login" element={<Page><Login /></Page>} />
            <Route path="/forgot-password" element={<Page><ForgotPassword /></Page>} />
            <Route path="/reset-password" element={<Page><ResetPassword /></Page>} />
            {/* Public legal pages (no auth) — needed by the Google OAuth consent
                screen and pre-signup review; copy lives in locales/<lang>/legal.ts. */}
            <Route path="/privacy" element={<Legal kind="privacy" />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
            {/* Public SEO pages — indexable; outside RequireAuth like Legal. */}
            <Route path="/diario-quirurgico" element={<Page><Guide /></Page>} />
            <Route path="/surgical-logbook" element={<Page><Guide /></Page>} />
            <Route path="/features" element={<Page><Features /></Page>} />
            <Route path="*" element={<Page><NotFound /></Page>} />
          </SentryRoutes>
        </Suspense>
        <AppToaster />
        <PwaUpdateBanner />
      </MotionConfig>
      </MotionProvider>
    </SuturiaProvider>
  );
}
